import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DateUtils from '../shared/DateUtils';
import { ISurveyStatistics } from '../shared/interfaces/ISurveyStatistics';

const COLORS = {
  total: '#03424f',
  promoter: '#74da67',
  yes: '#74DA6770',
  positive: 'rgba(159,218,103,0.44)',
  neutral: '#F7B419',
  detractor: '#E15E5EFF',
  no: '#E15E5E70',
  negative: 'rgba(225,94,133,0.44)',
};
interface ChartDataItem {
  name: string;
  surveys: number;
  color: string;
  mode: string;
  positive?: number | null;
  neutral?: number | null;
  negative?: number | null;
}
const count = (q: ISurveyStatistics['questions'][number], rating: number) =>
  q.ratingCounts.find((item) => item.rating === rating)?.count || 0;
const useChartData = (
  statistics: ISurveyStatistics,
  type: string,
  mode: string,
  customAnswers?: {
    answerOne: string;
    answerTwo: string;
    answerThree: string;
    answerFour: string;
    answerFive: string;
  }
) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  useEffect(() => {
    const reload = () => window.location.reload();
    i18n.on('languageChanged', reload);
    return () => i18n.off('languageChanged', reload);
  }, [i18n]);
  return useMemo<ChartDataItem[]>(() => {
    if (!statistics) return [];
    const total = statistics.total;
    if (mode === 'month')
      return statistics.monthly.map((m) => ({
        name: DateUtils.shortMonthByIndex(m.month - 1, language),
        positive: m.positive || null,
        neutral: m.neutral || null,
        negative: m.negative || null,
        surveys: m.total,
        color: COLORS.total,
        mode: 'month',
      }));
    if (mode === 'details' && type === 'STANDARD' && customAnswers) {
      return statistics.questions.flatMap((q, index) =>
        index === statistics.questions.length - 1
          ? [
              { name: t('yes'), surveys: count(q, 1), color: COLORS.yes, mode: 'details' },
              { name: t('no'), surveys: count(q, 0), color: COLORS.no, mode: 'details' },
            ]
          : [
              {
                name: customAnswers.answerOne,
                surveys: count(q, 5),
                color: COLORS.promoter,
                mode: 'details',
              },
              {
                name: customAnswers.answerTwo,
                surveys: count(q, 4),
                color: COLORS.positive,
                mode: 'details',
              },
              {
                name: customAnswers.answerThree,
                surveys: count(q, 3),
                color: COLORS.neutral,
                mode: 'details',
              },
              {
                name: customAnswers.answerFour,
                surveys: count(q, 2),
                color: COLORS.negative,
                mode: 'details',
              },
              {
                name: customAnswers.answerFive,
                surveys: count(q, 1),
                color: COLORS.detractor,
                mode: 'details',
              },
            ]
      );
    }
    if (mode !== t('total')) return [];
    const labels =
      type === 'NPS'
        ? [
            'Gesamtanzahl',
            'positiv (Score 9 + 10)',
            'neutral (Score 7 + 8)',
            'negativ (Score 0 - 6)',
          ]
        : type === 'CUSTOMSURVEY'
        ? ['Gesamtanzahl', 'beendet', 'abgebrochen']
        : ['Gesamtanzahl', 'positive', ...(type === 'BAROMETER' ? ['neutral'] : []), 'negative'];
    const values = [
      total.total,
      total.positive,
      ...(type === 'NPS' || type === 'BAROMETER' ? [total.neutral] : []),
      total.negative,
    ];
    const colors = [
      COLORS.total,
      COLORS.promoter,
      ...(type === 'NPS' || type === 'BAROMETER' ? [COLORS.neutral] : []),
      COLORS.detractor,
    ];
    return labels.map((name, i) => ({
      name,
      surveys: values[i],
      color: colors[i],
      mode: t('total'),
    }));
  }, [statistics, type, mode, customAnswers, t, language]);
};
export default useChartData;
